<template>
    <CalendarRoot
        v-if="calendarView === 'day'"
        v-bind="forwarded"
        v-model:placeholder="placeholder"
        v-slot="{ grid, weekDays }"
        :class="cn('w-60 px-3 py-2', props.class)"
    >
        <CalendarHeader>
            <CalendarPrevButton />
            <div class="flex flex-1 items-center justify-center gap-1">
                <DsButton
                    :disabled="props.disabled"
                    size="xs"
                    variant="secondary"
                    @click="calendarView = 'month'"
                >
                    {{ monthLabel }}
                </DsButton>
                <DsButton
                    :disabled="props.disabled"
                    size="xs"
                    variant="secondary"
                    @click="calendarView = 'year'"
                >
                    {{ yearLabel }}
                </DsButton>
            </div>
            <CalendarNextButton />
        </CalendarHeader>

        <div class="mt-4 flex flex-col gap-y-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
            <CalendarGrid
                v-for="month in grid"
                :key="`${month.value}`"
            >
                <CalendarGridHead>
                    <CalendarGridRow>
                        <CalendarHeadCell
                            v-for="day in weekDays"
                            :key="day"
                        >
                            {{ day }}
                        </CalendarHeadCell>
                    </CalendarGridRow>
                </CalendarGridHead>
                <CalendarGridBody>
                    <CalendarGridRow
                        v-for="(weekDates, index) in month.rows"
                        :key="`weekDate-${index}`"
                    >
                        <CalendarCell
                            v-for="weekDate in weekDates"
                            :key="`${weekDate}`"
                            :date="weekDate"
                        >
                            <CalendarCellTrigger
                                :day="weekDate"
                                :month="month.value"
                            />
                        </CalendarCell>
                    </CalendarGridRow>
                </CalendarGridBody>
            </CalendarGrid>
        </div>
    </CalendarRoot>

    <MonthPickerRoot
        v-else-if="calendarView === 'month'"
        v-model:placeholder="placeholder"
        v-slot="{ grid }"
        v-bind="sharedPickerProps"
        :class="cn('w-60 px-3 py-2', props.class)"
        @update:modelValue="onMonthSelected"
    >
        <CalendarHeader>
            <MonthPickerPrevButton />
            <div class="flex flex-1 items-center justify-center">
                <DsButton
                    :disabled="props.disabled"
                    size="xs"
                    variant="secondary"
                    @click="calendarView = 'year'"
                >
                    {{ yearLabel }}
                </DsButton>
            </div>
            <MonthPickerNextButton />
        </CalendarHeader>

        <MonthPickerGrid class="mt-4">
            <MonthPickerGridBody>
                <MonthPickerGridRow
                    v-for="(monthsRow, index) in grid.rows"
                    :key="`monthsRow-${index}`"
                >
                    <MonthPickerCell
                        v-for="monthDate in monthsRow"
                        :key="`${monthDate}`"
                        :date="monthDate"
                    >
                        <MonthPickerCellTrigger :month="monthDate" />
                    </MonthPickerCell>
                </MonthPickerGridRow>
            </MonthPickerGridBody>
        </MonthPickerGrid>
    </MonthPickerRoot>

    <YearPickerRoot
        v-else
        v-model:placeholder="placeholder"
        v-slot="{ grid }"
        v-bind="sharedPickerProps"
        :class="cn('w-60 px-3 py-2', props.class)"
        @update:modelValue="onYearSelected"
    >
        <CalendarHeader>
            <YearPickerPrevButton />
            <YearPickerHeading class="flex-1 text-center" />
            <YearPickerNextButton />
        </CalendarHeader>

        <YearPickerGrid class="mt-4">
            <YearPickerGridBody>
                <YearPickerGridRow
                    v-for="(yearsRow, index) in grid.rows"
                    :key="`yearsRow-${index}`"
                >
                    <YearPickerCell
                        v-for="yearDate in yearsRow"
                        :key="`${yearDate}`"
                        :date="yearDate"
                    >
                        <YearPickerCellTrigger :year="yearDate" />
                    </YearPickerCell>
                </YearPickerGridRow>
            </YearPickerGridBody>
        </YearPickerGrid>
    </YearPickerRoot>
</template>

<script setup lang="ts">
import DsButton from '@/components/design-system/button/DsButton.vue';
import CalendarCell from '@/components/design-system/form/date-picker/CalendarCell.vue';
import CalendarCellTrigger from '@/components/design-system/form/date-picker/CalendarCellTrigger.vue';
import CalendarGrid from '@/components/design-system/form/date-picker/CalendarGrid.vue';
import CalendarGridBody from '@/components/design-system/form/date-picker/CalendarGridBody.vue';
import CalendarGridHead from '@/components/design-system/form/date-picker/CalendarGridHead.vue';
import CalendarGridRow from '@/components/design-system/form/date-picker/CalendarGridRow.vue';
import CalendarHeadCell from '@/components/design-system/form/date-picker/CalendarHeadCell.vue';
import CalendarHeader from '@/components/design-system/form/date-picker/CalendarHeader.vue';
import CalendarNextButton from '@/components/design-system/form/date-picker/CalendarNextButton.vue';
import CalendarPrevButton from '@/components/design-system/form/date-picker/CalendarPrevButton.vue';
import { formatDateLabel, toSingleDate } from '@/components/design-system/form/date-picker/date-picker.utils';
import MonthPickerCell from '@/components/design-system/form/date-picker/MonthPickerCell.vue';
import MonthPickerCellTrigger from '@/components/design-system/form/date-picker/MonthPickerCellTrigger.vue';
import MonthPickerGrid from '@/components/design-system/form/date-picker/MonthPickerGrid.vue';
import MonthPickerNextButton from '@/components/design-system/form/date-picker/MonthPickerNextButton.vue';
import MonthPickerPrevButton from '@/components/design-system/form/date-picker/MonthPickerPrevButton.vue';
import YearPickerCell from '@/components/design-system/form/date-picker/YearPickerCell.vue';
import YearPickerCellTrigger from '@/components/design-system/form/date-picker/YearPickerCellTrigger.vue';
import YearPickerGrid from '@/components/design-system/form/date-picker/YearPickerGrid.vue';
import YearPickerHeading from '@/components/design-system/form/date-picker/YearPickerHeading.vue';
import YearPickerNextButton from '@/components/design-system/form/date-picker/YearPickerNextButton.vue';
import YearPickerPrevButton from '@/components/design-system/form/date-picker/YearPickerPrevButton.vue';
import { cn } from '@/lib/utils';
import { getLocalTimeZone, today } from '@internationalized/date';
import { reactiveOmit } from '@vueuse/core';
import type { CalendarRootEmits, CalendarRootProps, DateValue } from 'reka-ui';
import {
    CalendarRoot,
    MonthPickerGridBody,
    MonthPickerGridRow,
    MonthPickerRoot,
    YearPickerGridBody,
    YearPickerGridRow,
    YearPickerRoot,
    useForwardPropsEmits,
} from 'reka-ui';
import { computed, ref, type HTMLAttributes } from 'vue';

type CalendarView = 'day' | 'month' | 'year';

const props = defineProps<Omit<CalendarRootProps, 'placeholder'> & { class?: HTMLAttributes['class'] }>();

const emits = defineEmits<Omit<CalendarRootEmits, 'update:placeholder'>>();

const delegatedProps = reactiveOmit(props, 'class');

const forwarded = useForwardPropsEmits(delegatedProps, emits);

const sharedPickerProps = computed(() => ({
    disabled: props.disabled,
    readonly: props.readonly,
    minValue: props.minValue,
    maxValue: props.maxValue,
    locale: props.locale,
    calendarLabel: props.calendarLabel,
    dir: props.dir,
    initialFocus: props.initialFocus,
    modelValue: props.modelValue,
    preventDeselect: true,
}));

const calendarView = ref<CalendarView>('day');

const placeholder = defineModel<DateValue>('placeholder', { default: () => today(getLocalTimeZone()) });

const monthLabel = computed(() => formatDateLabel(placeholder.value, props.locale, { month: 'long' }));

const yearLabel = computed(() => formatDateLabel(placeholder.value, props.locale, { year: 'numeric' }));

const onMonthSelected = (date: DateValue | DateValue[] | undefined) => {
    const singleDate = toSingleDate(date);
    if (!singleDate) return;

    placeholder.value = placeholder.value.set({ year: singleDate.year, month: singleDate.month });
    calendarView.value = 'day';
};

const onYearSelected = (date: DateValue | DateValue[] | undefined) => {
    const singleDate = toSingleDate(date);
    if (!singleDate) return;

    placeholder.value = placeholder.value.set({ year: singleDate.year });
    calendarView.value = 'month';
};
</script>
